import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper, Modal,
  styled,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Stack,
  CircularProgress,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SearchIcon from "@mui/icons-material/Search";

const StyledPaper = styled(Paper)(({ theme, mode, customColors }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
  backgroundColor:
    mode === "dark"
      ? "#2A2A3C"
      : mode === "custom"
        ? customColors?.background || "#f5f5f5"
        : "#f0f7ff",
  borderRadius: "16px",
  boxShadow:
    mode === "dark"
      ? "0 8px 24px rgba(0, 0, 0, 0.3)"
      : "0 8px 24px rgba(30, 139, 175, 0.12)",
}));

// Palette setup
const palette = {
  light: {
    headerBg: "#e3f2fd",
    headerText: "#37474f",
    rowHover: "#f5f5f5",
    text: "#333",
  },
  dark: {
    headerBg: "#2A2A3C",
    headerText: "#EDEDED",
    rowHover: "#383C54",
    text: "#EDEDED",
  },
  custom: (customColors) => ({
    headerBg: customColors?.cardBg || "#f5f5f5",
    headerText: customColors?.text || "#111111",
    rowHover: customColors?.primary || "#e8f4ff",
    text: customColors?.text || "#111111",
  }),
};

export default function ClassPanel({
  mode,
  customColors,
  setSnackbar,
  isLoading,
  setIsLoading,
  currentUserRole,
}) {
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentClass, setCurrentClass] = useState({ id: null, name: "", description: "" });
  const [deleteClassId, setDeleteClassId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch classes from API
  useEffect(() => {
    const fetchClasses = async () => {
      setIsLoading(true);
      try {
        const token = sessionStorage.getItem("authToken");
        const response = await fetch(`${apiUrl}/admins/view-student-classes/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        const classesArray = Array.isArray(data) ? data : [];
        setClasses(classesArray);
        setFilteredClasses(classesArray);
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to fetch classes.",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchClasses();
  }, [apiUrl]);

  // Filter classes based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredClasses(classes);
    } else {
      const filtered = classes.filter(cls =>
        cls.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cls.description && cls.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredClasses(filtered);
    }
    setCurrentPage(1); // Reset to first page when searching
  }, [searchTerm, classes]);

  // Calculate pagination - FIXED: use Math.min to ensure correct slicing
  const indexOfLastRow = Math.min(currentPage * rowsPerPage, filteredClasses.length);
  const indexOfFirstRow = (currentPage - 1) * rowsPerPage;
  const currentClasses = filteredClasses.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredClasses.length / rowsPerPage);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle rows per page change - FIXED: Reset to page 1
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  // Handle search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle dialog open/close
  const handleDialogOpen = (cls = { id: null, name: "", description: "" }) => {
    setCurrentClass(cls);
    setOpenDialog(true);
  };
  const handleDialogClose = () => setOpenDialog(false);

  // Handle delete dialog open/close
  const handleOpenDeleteDialog = (id) => {
    setDeleteClassId(id);
    setOpenDeleteDialog(true);
  };
  const handleCloseDeleteDialog = () => {
    setDeleteClassId(null);
    setOpenDeleteDialog(false);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const token = sessionStorage.getItem("authToken");
      const url = currentClass.id
        ? `${apiUrl}/admins/student-classes/${currentClass.id}/`
        : `${apiUrl}/admins/student-classes/`;
      const method = currentClass.id ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(currentClass),
      });
      if (response.ok) {
        const updatedClasses = await fetch(`${apiUrl}/admins/view-student-classes/`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((res) => res.json());
        const classesArray = Array.isArray(updatedClasses) ? updatedClasses : [];
        setClasses(classesArray);
        setFilteredClasses(classesArray);
        setSnackbar({
          open: true,
          message: `Class ${currentClass.id ? "updated" : "added"} successfully.`,
          severity: "success",
        });
        handleDialogClose();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to save class.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await fetch(`${apiUrl}/admins/student-classes/${deleteClassId}/delete/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const updatedClasses = classes.filter((cls) => cls.id !== deleteClassId);
        setClasses(updatedClasses);
        setFilteredClasses(updatedClasses);
        setSnackbar({
          open: true,
          message: "Class deleted successfully.",
          severity: "success",
        });
        handleCloseDeleteDialog();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete class.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledPaper
      mode={mode}
      customColors={customColors}
      sx={{
        backgroundColor:
          mode === "dark"
            ? palette.dark.headerBg
            : mode === "custom"
              ? customColors?.bgNav || "#f5f5f5"
              : palette.light.headerBg,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          mb: 3,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          pb: 2,
          borderBottom:
            mode === "dark"
              ? "2px solid rgba(255, 255, 255, 0.1)"
              : "2px solid rgba(30, 139, 175, 0.15)",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontWeight: 700,
            letterSpacing: "-0.5px",
          }}
        >
          Classes
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          {/* Search Bar */}
          <TextField
            placeholder="Search classes..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{
                mr: 1,
                color: mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)"
              }} />,
            }}
            sx={{
              width: 250,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(30, 139, 175, 0.05)",
                '&:hover': {
                  backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(30, 139, 175, 0.08)",
                },
                '&.Mui-focused': {
                  backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(30, 139, 175, 0.1)",
                  boxShadow: "0 0 0 2px rgba(30, 139, 175, 0.2)",
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(30, 139, 175, 0.3)",
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.4)" : "#1e8bafff",
              },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: "#1e8bafff",
              },
            }}
          />

          {/* Add Class Button */}
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => handleDialogOpen()}
            sx={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
              color: "white",
              borderRadius: 3,
              fontWeight: 600,
              px: 3,
              py: 1,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(30, 58, 138, 0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #1a3278 0%, #1a7a9a 100%)",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(30, 58, 138, 0.4)",
              },
            }}
          >
            Add Class
          </Button>
        </Stack>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#1e8bafff" }} size={48} />
        </Box>
      ) : filteredClasses.length > 0 ? (
        <>
          <TableContainer
            sx={{
              width: "100%",
              borderRadius: "12px",
              overflow: "hidden",
              border:
                mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(30, 139, 175, 0.15)",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                  }}
                >
                  <TableCell
                    sx={{
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      py: 2,
                      textTransform: "uppercase",
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      py: 2,
                      textTransform: "uppercase",
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      py: 2,
                      textTransform: "uppercase",
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentClasses.map((cls, index) => (
                  <TableRow
                    key={cls.id}
                    sx={{
                      backgroundColor:
                        mode === "dark"
                          ? index % 2 === 0
                            ? "rgba(255, 255, 255, 0.03)"
                            : "transparent"
                          : index % 2 === 0
                            ? "rgba(30, 139, 175, 0.03)"
                            : "transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor:
                          mode === "dark"
                            ? "rgba(30, 139, 175, 0.1)"
                            : "rgba(30, 139, 175, 0.08)",
                        transform: "scale(1.01)",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            mode === "dark"
                              ? palette.dark.text
                              : mode === "custom"
                                ? customColors?.text || "#1e3a8a"
                                : "#1e3a8a",
                          fontWeight: 600,
                          fontSize: "0.95rem",
                        }}
                      >
                        {cls.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color:
                            mode === "dark"
                              ? "rgba(255, 255, 255, 0.7)"
                              : "rgba(0, 0, 0, 0.7)",
                          fontSize: "0.9rem",
                        }}
                      >
                        {cls.description}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                        <Tooltip title="Edit Class" arrow>
                          <IconButton
                            onClick={() => handleDialogOpen(cls)}
                            sx={{
                              color: "#1e8bafff",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "rgba(30, 139, 175, 0.1)",
                                transform: "scale(1.15)",
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Class" arrow>
                          <IconButton
                            onClick={() => handleOpenDeleteDialog(cls.id)}
                            sx={{
                              color: "#d32f2f",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.1)",
                                transform: "scale(1.15)",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls with Size Selector */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 4,
            flexWrap: 'wrap',
            gap: 2
          }}>
            {/* Page Info */}
            <Typography sx={{
              color: mode === 'dark' ? '#EDEDED' : '#37474f',
              fontWeight: 500,
              fontSize: '0.9rem',
            }}>
              Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredClasses.length)} of {filteredClasses.length} classes
            </Typography>

            {/* Pagination Controls */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}>
              {/* Rows per page selector */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  sx={{
                    borderRadius: 3,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1e8bafff',
                    },
                    '& .MuiSelect-select': {
                      py: 0.75,
                      px: 2,
                    },
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(30, 139, 175, 0.05)',
                  }}
                >
                  <MenuItem value={5}>5 per page</MenuItem>
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={15}>15 per page</MenuItem>
                  <MenuItem value={20}>20 per page</MenuItem>
                  <MenuItem value={25}>25 per page</MenuItem>
                </Select>
              </FormControl>

              {/* Prev/Next buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  startIcon={<ArrowBackIosIcon sx={{ fontSize: '0.9rem' }} />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 3,
                    borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    color: mode === 'dark' ? '#EDEDED' : '#37474f',
                    minWidth: 90,
                    '&:hover': {
                      borderColor: '#1e8bafff',
                      backgroundColor: mode === 'dark' ? 'rgba(30, 139, 175, 0.1)' : 'rgba(30, 139, 175, 0.05)',
                    },
                    '&.Mui-disabled': {
                      borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  Prev
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  endIcon={<ArrowForwardIosIcon sx={{ fontSize: '0.9rem' }} />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 3,
                    borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    color: mode === 'dark' ? '#EDEDED' : '#37474f',
                    minWidth: 90,
                    '&:hover': {
                      borderColor: '#1e8bafff',
                      backgroundColor: mode === 'dark' ? 'rgba(30, 139, 175, 0.1)' : 'rgba(30, 139, 175, 0.05)',
                    },
                    '&.Mui-disabled': {
                      borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            opacity: 0.7,
          }}
        >
          <SearchIcon sx={{ fontSize: 64, color: "#1e8bafff", mb: 2, opacity: 0.5 }} />
          <Typography
            variant="h6"
            sx={{
              color: mode === "dark" ? palette.dark.text : "#37474f",
              fontWeight: 500,
            }}
          >
            {searchTerm ? "No matching classes found" : "No classes found"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
              mt: 1,
            }}
          >
            {searchTerm ? "Try a different search term" : "Add a class to get started"}
          </Typography>
        </Box>
      )}

      {/* Dialog for Add/Edit */}
      <Modal
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="class-dialog-title"
        aria-describedby="class-dialog-description"
        sx={{
          backdropFilter: "blur(4px)",
          zIndex: 1500,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 500 },
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: "20px",
            boxShadow: mode === "dark"
              ? "0 20px 60px rgba(0, 0, 0, 0.5)"
              : "0 20px 60px rgba(30, 58, 138, 0.15)",
            border: mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(30, 139, 175, 0.1)",
            background: mode === "dark"
              ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
              : mode === "custom"
                ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
            overflow: "hidden",
          }}
        >
          {/* Sticky Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: mode === "dark"
                ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                : mode === "custom"
                  ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                  : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
              p: 4,
              pb: 2,
              borderRadius: "20px 20px 0 0",
              boxShadow: mode === "dark"
                ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                : "0 4px 12px rgba(30, 139, 175, 0.1)",
            }}
          >
            <Typography
              id="class-dialog-title"
              variant="h5"
              sx={{
                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                mb: 1,
                fontWeight: 700,
                letterSpacing: "-0.5px",
              }}
            >
              {currentClass.id ? "Edit Class" : "Add Class"}
            </Typography>
            <Typography
              id="class-dialog-description"
              variant="body2"
              sx={{
                color: mode === "dark"
                  ? "rgba(255, 255, 255, 0.6)"
                  : mode === "custom"
                    ? customColors?.text
                      ? `${customColors.text}99`
                      : "rgba(0, 0, 0, 0.6)"
                    : "rgba(0, 0, 0, 0.6)",
                fontSize: "0.875rem",
              }}
            >
              {currentClass.id ? "Update the class details below" : "Fill in the details to add a new class"}
            </Typography>

          </Box>

          {/* Scrollable Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 3,
              // Scrollbar styling
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: mode === "dark"
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(30, 139, 175, 0.3)",
                borderRadius: "4px",
              },
            }}
          >
            <TextField
              autoFocus
              margin="dense"
              label="Class Name"
              fullWidth
              value={currentClass.name}
              onChange={(e) => setCurrentClass({ ...currentClass, name: e.target.value })}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(30, 139, 175, 0.05)",
                },
              }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={currentClass.description}
              onChange={(e) => setCurrentClass({ ...currentClass, description: e.target.value })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(30, 139, 175, 0.05)",
                },
              }}
            />
          </Box>

          {/* Sticky Footer */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              background: mode === "dark"
                ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                : mode === "custom"
                  ? `linear-gradient(135deg, ${customColors?.background || "#f5f5f5"} 0%, ${customColors?.primary || "#1e8bafff"}15 100%)`
                  : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
              p: 2,
              borderRadius: "0 0 20px 20px",
              boxShadow: mode === "dark"
                ? "0 -4px 12px rgba(0, 0, 0, 0.3)"
                : "0 -4px 12px rgba(30, 139, 175, 0.1)",
              borderTop: mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(30, 139, 175, 0.1)",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              onClick={handleDialogClose}
              sx={{
                color: mode === "dark" ? "#ffffff" : "#1e3a8a",
                borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(30, 139, 175, 0.3)",
                textTransform: "none",
                borderRadius: "12px",
                fontWeight: 600,
                px: 3,
                py: 1.25,
                "&:hover": {
                  borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(30, 139, 175, 0.5)",
                  backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(30, 139, 175, 0.05)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                color: "white",
                fontWeight: 600,
                fontSize: "0.95rem",
                textTransform: "none",
                px: 3,
                py: 1.25,
                borderRadius: "12px",
                boxShadow: "0 4px 14px rgba(30, 139, 175, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #1e3a8a 0%, #1a7a9a 100%)",
                  boxShadow: "0 6px 20px rgba(30, 139, 175, 0.4)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0px)",
                  boxShadow: "0 2px 8px rgba(30, 139, 175, 0.3)",
                },
              }}
            >
              {currentClass.id ? "Update" : "Add"}
            </Button>
          </Box>
        </Box>
      </Modal>


      {/* Dialog for Delete */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 3,
            width: "400px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.2rem" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this class? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              color: mode === "dark" ? palette.dark.headerText : "#1e3a8a",
              borderColor: mode === "dark" ? palette.dark.headerText : "#1e3a8a",
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </StyledPaper>
  );
}